'use client';

import {motion} from 'motion/react';
import type {ReactNode} from 'react';

const FADE_EASE = [0.16, 1, 0.3, 1] as const;

export function FadeIn({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      whileInView={{opacity: 1, y: 0}}
      viewport={{once: false, margin: '-50px'}}
      transition={{duration: 0.7, delay, ease: FADE_EASE}}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/** @deprecated Use FadeIn instead */
export function RevealSection({
  children,
  className = '',
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <FadeIn delay={delay} className={className}>
      {children}
    </FadeIn>
  );
}

export const cardHoverClass =
  'transition-all duration-300 hover:shadow-xl hover:-translate-y-1';

export const sectionPadding = 'py-24';
export const sectionBorderLight = 'border-y border-kobra-border';
export const sectionBorderDark = 'border-y border-kobra-border-dark';

export function SectionBadge({
  children,
  dark = false,
}: {
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-widest uppercase mb-8 ${
        dark
          ? 'bg-kobra-surface border border-kobra-border-dark text-kobra-accent'
          : 'bg-white border border-kobra-border text-kobra-accent'
      }`}
    >
      {children}
    </div>
  );
}

export function SectionContainer({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
  );
}

export function SectionHeader({
  title,
  description,
  badge,
  align = 'center',
  dark = false,
}: {
  title: string;
  description?: string;
  badge?: string;
  align?: 'center' | 'left';
  dark?: boolean;
}) {
  return (
    <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 lg:mb-16`}>
      {badge ? (
        <SectionBadge dark={dark}>{badge}</SectionBadge>
      ) : null}
      <h2
        className={`text-3xl md:text-4xl font-bold mb-4 ${dark ? 'text-white' : 'text-kobra-text-dark'}`}
      >
        {title}
      </h2>
      {description ? (
        <p className={`text-lg leading-relaxed ${dark ? 'text-kobra-text-muted-light' : 'text-kobra-text-muted'}`}>
          {description}
        </p>
      ) : null}
    </div>
  );
}
